import { Link } from 'react-router';
import { TriangleAlert, X } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { buttonVariants } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useDismissibleUsageAlert } from '@/hooks/use-dismissible-usage-alert';
import { usageAlertTier, usageAlertTone } from '@/lib/usage-alert';
import { orgUrl } from '@/lib/org-url';
import { cn } from '@/lib/utils';

interface SidebarUsageAlertProps {
  /** Dismissal key + billing-link target; null hides the CTA. */
  orgId: string | null;
  percentage: number;
  /** Frozen overrides the percentage for tier + tone (>= the 100 band). */
  frozen: boolean;
  message: string;
  /** Render the CTA (admins only). */
  showCta: boolean;
  /**
   * Where the CTA sends them, and what it says. Defaults to the Plans page,
   * which is right for a tier problem and WRONG for an unpaid first invoice:
   * a live `incomplete` subscription cannot start another Checkout, so
   * "Upgrade Plan" pointed at a page that could not help (Codex, PR #221).
   */
  ctaTarget?: 'plans' | 'billing';
  ctaLabel?: string;
}

/**
 * The shared sidebar usage banner used by BOTH the org and workspace sidebars
 * (the only two surfaces). Surface-specific message/role/frozen logic lives in
 * the thin callers (OrgUsageBanner, WorkspaceUsageNotice); palette is the single
 * usageAlertTone source. Approaching-limit is a warning, never blue info.
 *
 * Dismissible per-tier below 100 (see useDismissibleUsageAlert): the X hides the
 * banner at its current level but it returns if usage climbs into a higher tier.
 * The 100 tier (out of quota / paused) has no X (AIT-636). Collapsed
 * icon-rail shows just the warning glyph (message in a tooltip) — no room for the
 * full banner.
 */
export function SidebarUsageAlert({
  orgId,
  percentage,
  frozen,
  message,
  showCta,
  ctaTarget = 'plans',
  ctaLabel = 'Upgrade Plan',
}: SidebarUsageAlertProps) {
  const tier = usageAlertTier(percentage, frozen);
  const { dismissed, dismiss } = useDismissibleUsageAlert(orgId, tier);
  // AIT-636: out of quota (or channels paused) is not a nudge, it is "delivery
  // is stopped". No X, and a dismissal stored earlier for this tier is ignored.
  const dismissible = tier < 100;

  if (tier === 0 || (dismissible && dismissed)) return null;

  const tone = usageAlertTone(percentage, frozen);
  // Upgrade goes to Plans (tier selection), not Billing (payment/invoices) —
  // they were split 2026-06-18 and tier changes live on the Plans page.
  const upgradeTo = showCta && orgId ? orgUrl(orgId, ctaTarget) : null;

  return (
    <>
      {/* Expanded: full warning banner (hidden on the collapsed icon rail). */}
      <Alert className={`${tone.surface} group-data-[collapsible=icon]:hidden`}>
        <TriangleAlert className={tone.icon} />
        <div className="flex items-start justify-between gap-1.5">
          <AlertDescription className={`min-w-0 text-xs ${tone.text}`}>
            {message}
          </AlertDescription>
          {dismissible && (
            <button
              type="button"
              aria-label="Dismiss"
              onClick={dismiss}
              className={`-mr-0.5 shrink-0 opacity-70 transition-opacity hover:opacity-100 ${tone.text}`}
            >
              <X className="size-3.5" />
            </button>
          )}
        </div>
        {upgradeTo && (
          <Link
            to={upgradeTo}
            className={cn(
              buttonVariants({ size: 'sm', variant: 'outline' }),
              'mt-1 h-7 justify-self-start text-xs',
            )}
          >
            {ctaLabel}
          </Link>
        )}
      </Alert>

      {/* Collapsed icon rail: warning glyph only, message (+ CTA) on hover. */}
      <div className="hidden justify-center group-data-[collapsible=icon]:flex">
        <Tooltip>
          <TooltipTrigger
            render={
              upgradeTo ? (
                <Link
                  to={upgradeTo}
                  aria-label={message}
                  className={`inline-flex rounded-md p-1.5 transition-opacity hover:opacity-80 ${tone.text}`}
                />
              ) : (
                <span aria-label={message} className={`inline-flex rounded-md p-1.5 ${tone.text}`} />
              )
            }
          >
            <TriangleAlert className="size-4" />
          </TooltipTrigger>
          <TooltipContent side="right" className="max-w-56">
            {message}
          </TooltipContent>
        </Tooltip>
      </div>
    </>
  );
}
